// Data Health — the write path every fix goes through.
//
// Every Data Health fix (set cost, set sku, correct inventory, activate,
// toggle physical/tracking, assign location, price-below-cost dual edit)
// is the same wire concern: turn a set of edited rows into one or more
// POSTs to the owning ViewSet's canonical `bulk-update` / `bulk-create`
// action, negotiate the two server-side confirmation gates, chunk past the
// per-request cap, and report which ids landed AND which the server now
// considers resolved.
//
// The interface is `resolveIssue(...)` plus the pure `buildItem(...)` helper;
// the body hides chunking, the 409 PO-impact gate, the 200 currency-adoption
// gate, per-row result folding, and the carry-forward of a confirmed strategy
// across chunks. The caller injects how a request reaches the network
// (`transport`) and how a gate is confirmed with the merchant (`confirm`), and
// gets back a normalized `{ successIds, resolvedIds, failedIds, ... }` outcome.
//
// One Data Health fix is NOT a bulk write: `stale_placement`'s Remove is a
// per-row DELETE on the owning viewset, guarded server-side. Its endpoint and
// its one refusal code live here too (`rowEndpointFor` / `PLACEMENT_IN_USE`).
//
// `successIds` and `resolvedIds` are NOT the same set and must not be used
// interchangeably. `successIds` = the write landed. `resolvedIds` = the server
// re-checked the issue's population and this row is no longer in it — the only
// thing that licenses removing a row from a Data Health list. They diverge for
// price-below-cost, where clearing the issue depends on an FX-aware cost/price
// comparison only the server can make (a successful price write may still be
// below cost). The batch is tagged with `data_health_issue` so the endpoint
// knows which population to re-check; see
// `BulkOperationMixin.data_health_resolution` in backend/api/views/viewsets.py.
//
// The registry (viewset slug, wire field, fixed value, actionType) is the
// SoT for WHICH endpoint + payload a given issueKey resolves through — see
// ./registry.js.

import { ISSUE_DEFS, issueDef } from './registry';
import { categoryWrite } from '../utils/categoryEditing';
import { extractStructuredErrorCode } from '../utils/parseApiError';
import { costGatePayloadDefect, poGateDefect } from '../utils/writeGates';

// Client-side chunk size. Matches ItemViewSet.bulk_update's server cap so a
// "Show more" buffer larger than the cap still saves cleanly in sequence.
export const BULK_CHUNK_SIZE = 200;

/** assign-location births an ItemLocation row (bulk-create) rather than
 *  patching one (bulk-update) — the detail rows are Items with no placement
 *  yet, so there is nothing to update. Internal: callers ask `buildItem` for
 *  the wire shape and `endpointFor` for the URL rather than branching
 *  themselves, so create-vs-update is decided in exactly one place. */
function isCreateIssue(issueKey) {
  return ISSUE_DEFS[issueKey]?.actionType === 'assign-location';
}

/** Canonical endpoint for an issue's fix. */
export function endpointFor(issueKey) {
  const def = ISSUE_DEFS[issueKey];
  const verb = isCreateIssue(issueKey) ? 'bulk-create' : 'bulk-update';
  return `/api/${def.viewset}/${verb}/`;
}

/**
 * Endpoint for a `delete-row` issue's per-row DESTROY. The detail row's `id`
 * IS the owning viewset's pk (see `_serialize_item_location` in
 * backend/api/views/data_health.py, which emits the ItemLocation pk as `id`).
 */
export function rowEndpointFor(issueKey, rowId) {
  const def = ISSUE_DEFS[issueKey];
  return `/api/${def.viewset}/${rowId}/`;
}

/**
 * The structured code `ItemLocationViewSet.destroy` refuses with (HTTP 409)
 * when the placement still holds stock, an inbound purchase order, or an open
 * transfer (backend/api/views/item_locations.py).
 *
 * It is INFORMATION, not a failure: the merchant can clear the blockers and
 * try again. The page turns it into copy by code (`translateRequestError` →
 * `apiErrors.<code>`), never by the bare 409, which every other refusal of
 * that endpoint would share.
 */
export const PLACEMENT_IN_USE = 'placement_in_use';

/**
 * Thrown on a hard (non-gate) failure of a chunk POST. Carries the structured
 * error code so callers can translate via the `apiErrors.<code>` convention.
 */
export class ResolveError extends Error {
  constructor({ status, body }) {
    super(`Data Health save failed (HTTP ${status})`);
    this.name = 'ResolveError';
    this.status = status;
    this.body = body ?? null;
    this.code = extractStructuredErrorCode(body);
  }
}

/**
 * Build the `fields:{...}` bulk-update patch for one row from a normalized
 * value bag. This is the shared field/coercion semantic — the one place that
 * knows price-below-cost carries two fields, category writes send the picked
 * PK, quantity/lead-time/day coerce to int, and instant toggles emit their
 * fixed value regardless of the bag.
 *
 * Internal: `buildItem` is the exported way in, so no caller has to know that
 * one issue posts a create body instead of a `fields` patch.
 *
 * @param {string} issueKey
 * @param {object} bag  see `buildItem`
 * @param {object} [opts]  see `buildItem`
 * @returns {object} `fields` — may be empty (`buildItem` filters those out).
 */
function buildFields(issueKey, bag = {}, opts = {}) {
  const def = issueDef(issueKey);
  if (!def) return {};

  if (def.actionType === 'price-below-cost') {
    const fields = {};
    if (bag.cost != null && String(bag.cost).trim() !== '') {
      fields.current_cost_price_supplier_ccy = bag.cost;
    }
    if (bag.sell != null && String(bag.sell).trim() !== '') {
      fields.current_sell_price = bag.sell;
    }
    return fields;
  }

  if (def.actionType === 'category-edit') {
    // WHICH field a category write lands on is the store's to say, so this
    // asks the capability rather than the def: `product_category_id` (the
    // picked node's GID) on a taxonomy store, `item_type` (the type the
    // category is derived from) on a product-type one. An empty pick yields
    // an empty patch — a blank category IS the defect being fixed, so it is
    // never a write.
    if (!opts.categoryEditing) {
      throw new Error(
        `buildItem('${issueKey}') needs the store's category_editing `
        + 'capability — which field the fix writes depends on it',
      );
    }
    return categoryWrite(opts.categoryEditing, bag.category);
  }

  // Instant actions carry a fixed value, no user-typed input.
  if (def.actionType === 'toggle' || def.actionType === 'activate') {
    return { [def.fieldName]: def.fixedValue };
  }

  const raw = bag.value;
  if (raw == null || String(raw).trim() === '') return {};

  // Integer-typed fields: bulk_update's serializer rejects a string where an
  // IntegerField is expected.
  const isInt = def.actionType === 'quantity-edit';
  return { [def.fieldName]: isInt ? parseInt(raw, 10) : raw };
}

/**
 * Build ONE wire item for a `resolveIssue` batch, from a row id and the new
 * value(s) the caller read out of its own input store.
 *
 * The one place that knows an issue's fix is a CREATE rather than a patch:
 * `assign-location`'s detail rows are Items with no ItemLocation yet, so its
 * item carries `{item_id, location_id}` for bulk-create instead of a `fields`
 * patch.
 *
 * `id` rides on the create body too: bulk-create keys its per-row results by
 * `item_id`, so the whole outcome-folding path downstream (successIds,
 * resolvedIds, the reconcile rule) stays keyed on the row exactly as it is for
 * every other issue.
 *
 * @param {string} issueKey
 * @param {string|number} id  the detail row's id
 * @param {object} bag  Normalized new values. Consumed keys by actionType:
 *     assign-location   → { locationId }  (the picked Location's pk)
 *     price-below-cost  → { cost?, sell? }
 *     category-edit     → { category }  (whatever the store's category cell
 *                         holds: the picked taxonomy node on a taxonomy store,
 *                         the product type string on a product-type one)
 *     toggle | activate → (ignored — fixedValue drives the write)
 *     everything else   → { value }
 * @param {object} [opts]
 * @param {object} [opts.categoryEditing] the store's capability
 *   (`useCategoryEditing()`) — required for `missing_product_category`, whose
 *   fix field it names. Every other issue ignores it.
 * @returns {object|null} the wire item, or null when the bag carries nothing
 *   to write (a blank input) — callers filter those out rather than post a
 *   row the server would have to reject or no-op.
 */
export function buildItem(issueKey, id, bag = {}, opts = {}) {
  if (isCreateIssue(issueKey)) {
    const locationId = bag.locationId;
    if (locationId == null || String(locationId).trim() === '') return null;
    return { item_id: id, location_id: locationId, id };
  }

  const fields = buildFields(issueKey, bag, opts);
  return Object.keys(fields).length === 0 ? null : { id, fields };
}

function chunk(items, size) {
  const out = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * Resolve one Data Health issue for a batch of already-built items.
 *
 * @param {object} opts
 * @param {string} opts.issueKey
 * @param {object[]} opts.items
 *   Built by `buildItem` from the caller's own input store — update issues get
 *   `[{ id, fields }]`, the create issue (assign-location) gets
 *   `[{ item_id, location_id, id }]`. Ids MUST be deduped — the backend
 *   rejects duplicate ids in one batch.
 * @param {{ post: (endpoint: string, payload: object) => Promise<{status:number, body:any}> }} opts.transport
 *   Sends a chunk and resolves the raw HTTP status + parsed body for ANY
 *   response (including the 409 PO gate). Throws only on network failure.
 * @param {object} opts.confirm
 * @param {(info: {count:number}) => Promise<boolean>} opts.confirm.poDeletion
 *   Prompt to drop N pending PO recommendation lines invalidated by the edit.
 *   Resolve true to proceed, false to abort the whole save.
 * @param {(info: {changeContext:object, affectedItems:object[], retry:Function}) => Promise<boolean>} opts.confirm.currencyChange
 *   Host the cost-of-record adoption gate. Call `retry(strategy)` to re-issue
 *   the SAME chunk with the chosen strategy; it resolves
 *   `{ ok } | { requiresConfirmationAgain } | { error }`. Resolve true once
 *   confirmed + applied, false to abort.
 *
 * @returns {Promise<{
 *   successIds: Set<string>, resolvedIds: Set<string>, failedIds: Set<string>,
 *   failedRows: object[], cancelled: boolean,
 * }>}  `resolvedIds` are the rows the SERVER re-checked out of the issue (see
 *   the module header — not the same as `successIds`). `failedRows` are the
 *   server's failed result rows, each with its reason — show them through
 *   utils/failedRows.js, or a merchant hitting a whole-population refusal
 *   only learns that N rows failed. On `cancelled: true` the caller made no
 *   visible change and should keep the dirty state intact.
 */
export async function resolveIssue({ issueKey, items, transport, confirm }) {
  const endpoint = endpointFor(issueKey);
  const allIds = new Set(items.map((i) => String(i.id)));
  const failedIds = new Set();
  const resolvedIds = new Set();
  const failedRows = [];

  const foldResults = (body) => {
    body.results.forEach((r) => {
      if (!r.success) {
        failedIds.add(String(r.id));
        failedRows.push(r);
      }
    });

    // Every endpoint reachable from here answers a `data_health_issue`-tagged
    // batch with `resolved_ids`. A missing field means the endpoint dropped
    // the contract — fail loud rather than silently reporting nothing resolved
    // (which reads to the merchant as "the fix didn't work").
    if (!Array.isArray(body?.resolved_ids)) {
      throw new Error(
        `${endpoint} answered a data_health_issue batch without resolved_ids`,
      );
    }
    body.resolved_ids.forEach((id) => resolvedIds.add(String(id)));
  };

  // Aborting at a gate abandons the REMAINING chunks; earlier ones already
  // landed. `successIds` stays empty (the caller must keep every row dirty —
  // it can't tell which half committed), but `resolvedIds` is carried through:
  // those rows were re-checked out of the issue by the server, and hiding that
  // would leave fixed rows on screen with no path back except a full reload.
  const cancelledOutcome = () => ({
    successIds: new Set(), resolvedIds, failedIds, failedRows, cancelled: true,
  });

  const chunks = chunk(items, BULK_CHUNK_SIZE);

  // A merchant's single choice applies to every chunk in this save: prompt
  // once, then carry the decision forward. `confirmBody` is the modal's
  // confirmation fragment ({ confirm_cost_change } for an item adopt,
  // { currency_change_strategy } for a supplier reinterpret) — spread as-is so
  // this transport stays agnostic to which gate the endpoint raised.
  let confirmPoDeletion = false;
  let confirmBody = null;

  for (let i = 0; i < chunks.length; i++) {
    const payload = {
      // Tags the batch so the endpoint re-checks THIS issue's population and
      // returns `resolved_ids` alongside the per-row write results.
      data_health_issue: issueKey,
      items: chunks[i],
      ...(confirmPoDeletion ? { confirm_po_deletion: true } : {}),
      ...(confirmBody || {}),
    };

    const { status, body } = await transport.post(endpoint, payload);

    // --- PO-impact gate (HTTP 409). Prompt once; retry the same chunk. ---
    if (status === 409 && body?.confirmation_required && !confirmPoDeletion) {
      // The count IS the prompt ("N recommendations will be removed"), so a
      // missing count fails loud rather than asking about zero.
      // `poGateDefect` (utils/writeGates.js) is the rule for what makes it
      // askable.
      const count = body.pending_po_count;
      const poDefect = poGateDefect(count);
      if (poDefect) {
        throw new Error(`${endpoint} raised the PO-impact gate but ${poDefect}`);
      }
      const proceed = await confirm.poDeletion({ count });
      if (!proceed) return cancelledOutcome();
      confirmPoDeletion = true;
      i -= 1;
      continue;
    }

    if (status >= 400) throw new ResolveError({ status, body });

    // --- Cost-of-record adoption gate (HTTP 200 + requires_confirmation). ---
    if (body?.requires_confirmation) {
      // This dialog asks the merchant to approve a change to what their stock
      // is worth. A payload missing the items or the currency pair is a broken
      // contract, not a confirmation to render with the gaps filled in —
      // `costGatePayloadDefect` is the rule for what "renderable" means, and
      // CurrencyChangeConfirmModal applies it too.
      const defect = costGatePayloadDefect(body.change_context, body.affected_items);
      if (defect) {
        throw new Error(`${endpoint} raised the cost-of-record gate but ${defect}`);
      }
      const confirmed = await confirm.currencyChange({
        changeContext: body.change_context,
        affectedItems: body.affected_items,
        retry: async (cb) => {
          const r = await transport.post(endpoint, { ...payload, ...cb });
          if (r.body?.requires_confirmation) return { requiresConfirmationAgain: true };
          if (r.status >= 400) return { error: new ResolveError({ status: r.status, body: r.body }) };
          confirmBody = cb;
          foldResults(r.body);
          return { ok: true };
        },
      });
      if (!confirmed) return cancelledOutcome();
      continue; // this chunk was applied inside the retry
    }

    foldResults(body);
  }

  const successIds = new Set([...allIds].filter((id) => !failedIds.has(id)));
  return {
    successIds, resolvedIds, failedIds, failedRows, cancelled: false,
  };
}
